import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, ClipboardList, Users, Tag, Tags, MapPin,
  Banknote, Settings, LogOut, Menu, MessageSquare, History,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCompanySettings } from '../../hooks/useCompanyData';
import { useUnreadFeedback } from '../../hooks/useUnreadFeedback';
import { buildLogoUrl } from '../../utils/logo';
import FlooraLogo from '../common/FlooraLogo';
import './AdminLayout.css';

const NAV_MAIN = [
  { to: '/admin', label: 'nav.dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/production-replay', label: 'nav.productionReplay', icon: History },
  { to: '/admin/tasks', label: 'nav.tasks', icon: ClipboardList },
];

const NAV_CONFIG = [
  { to: '/admin/users', label: 'nav.users', icon: Users },
  { to: '/admin/task-categories', label: 'nav.categories', icon: Tag },
  { to: '/admin/task-tags', label: 'nav.taskTags', icon: Tags },
  { to: '/admin/rooms', label: 'nav.rooms', icon: MapPin },
];

const NAV_ADMIN = [
  { to: '/admin/labour-rates', label: 'nav.labourRates',   icon: Banknote },
  { to: '/admin/settings',     label: 'nav.adminSettings', icon: Settings },
  { to: '/admin/feedback',     label: 'nav.feedback',      icon: MessageSquare },
];

function NavGroup({ items, label, badges = {} }) {
  const { t } = useTranslation();
  const labelId = label ? `nav-group-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined;
  return (
    <div className="nav-group" role="group" aria-labelledby={labelId}>
      {label && <span className="nav-group-label" id={labelId}>{label}</span>}
      {items.map(({ to, label: lbl, icon: Icon, end }) => {
        const count = badges[to] ?? 0;
        return (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
          >
            <span className="sidebar-link-icon" aria-hidden="true">
              <Icon size={17} strokeWidth={1.75} />
            </span>
            <span className="sidebar-link-label">{t(lbl)}</span>
            {count > 0 && (
              <span className="sidebar-nav-badge" aria-label={t('a11y.unreadCount', { count })}>
                {count > 99 ? '99+' : count}
              </span>
            )}
          </NavLink>
        );
      })}
    </div>
  );
}

function SidebarBrand({ logoUrl, logoSize }) {
  const { t } = useTranslation();
  if (logoUrl) {
    return (
      <div className="sidebar-brand">
        <div className="sidebar-brand-mark sidebar-brand-mark--custom">
          <img
            src={logoUrl}
            alt={t('a11y.companyLogo')}
            className="sidebar-brand-logo"
            style={{ height: logoSize, maxWidth: 160 }}
          />
        </div>
      </div>
    );
  }
  return (
    <div className="sidebar-brand">
      <div className="sidebar-brand-mark">
        <FlooraLogo color="brand" height={26} />
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { t } = useTranslation();
  const { logout, profile } = useAuth();
  const { settings } = useCompanySettings();
  const { unreadCount } = useUnreadFeedback();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navBadges = unreadCount > 0 ? { '/admin/feedback': unreadCount } : {};

  const logoUrl = buildLogoUrl(settings?.logoUrl || null, settings?.logoVersion || null);
  const logoSize = settings?.logoSize ?? 36;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="admin-layout">
      {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar} />}

      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <SidebarBrand logoUrl={logoUrl} logoSize={logoSize} />

        <nav className="sidebar-nav" aria-label={t('a11y.mainNavigation')} onClick={closeSidebar}>
          <NavGroup items={NAV_MAIN} />
          <NavGroup items={NAV_CONFIG} label={t('navGroups.configure')} />
          <NavGroup items={NAV_ADMIN} label={t('navGroups.admin')} badges={navBadges} />
        </nav>

        <div className="sidebar-footer">
          <NavLink
            to="/settings/profile"
            className={({ isActive }) => `sidebar-link sidebar-link-profile ${isActive ? 'active' : ''}`}
            onClick={closeSidebar}
          >
            <div className="sidebar-avatar">{profile?.displayName?.[0]?.toUpperCase() ?? '?'}</div>
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">{profile?.displayName}</span>
              <span className="sidebar-user-role">{t('users.admin')}</span>
            </div>
          </NavLink>
          <button className="btn btn-icon sidebar-logout" onClick={handleLogout} title={t('common.logout')}>
            <LogOut size={17} />
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <div className="admin-mobile-topbar">
          <button className="btn btn-icon" onClick={() => setSidebarOpen(true)}>
            <Menu size={20} />
          </button>
          <div className="mobile-brand">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={t('a11y.companyLogo')}
                style={{ height: Math.min(logoSize, 28), maxWidth: 120, objectFit: 'contain' }}
              />
            ) : (
              <FlooraLogo color="brand" height={22} />
            )}
          </div>
        </div>
        <Outlet />
      </div>
    </div>
  );
}
